'use client';

import { useEffect, useMemo, useState } from 'react';

import { cn } from '@/libs';

import { Button } from '../ui/Button';
import { getPaginationData, Pagination } from '../ui/Pagination';

type Tab = {
  tabLabel: string;
  tabValue: string;
  tabBody: React.ReactNode;
};

type PaginationConfig = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  maxVisible?: number;
  loading?: boolean;
};

type SlidingTabProps = {
  tabs: Tab[];
  defaultTab?: string;
  onTabChange?: (tabValue: string) => void;
  pagination?: PaginationConfig;
  paginationClassName?: string;
  paginationMessage?: string;
  renderPagination?: (config: {
    pages: number[];
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    canGoPrevious: boolean;
    canGoNext: boolean;
    goToPrevious: () => void;
    goToNext: () => void;
  }) => React.ReactNode;
};

export const SlidingTab = ({
  tabs,
  defaultTab,
  onTabChange,
  pagination,
  paginationClassName,
  paginationMessage,
  renderPagination,
}: SlidingTabProps) => {
  const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.tabValue || '');

  const activeTabData = useMemo(
    () => tabs.find((tab) => tab.tabValue === activeTab) || tabs[0],
    [tabs, activeTab],
  );

  const paginationData = useMemo(
    () => (pagination ? getPaginationData(pagination) : null),
    [pagination],
  );

  // Mantém a aba ativa apenas se ainda existir nas tabs

  useEffect(() => {
    const current = tabs.find((t) => t.tabValue === activeTab)?.tabValue;
    if (!current && tabs[0]) {
      setActiveTab(tabs[0].tabValue);
    }
  }, [tabs, activeTab]);

  useEffect(() => {
    if (activeTab) {
      onTabChange?.(activeTab);
    }
  }, [activeTab, onTabChange]);

  const renderDefaultPagination = (data: NonNullable<typeof paginationData>) => (
    <div className={cn('justify-between items-end w-full pt-4', paginationClassName)}>
      {paginationMessage && (
        <div className="text-xs text-foreground md:p-0">{paginationMessage}</div>
      )}
      <Pagination
        currentPage={data.currentPage}
        totalPages={data.totalPages}
        onPageChange={data.onPageChange}
        maxVisible={pagination?.maxVisible}
        loading={data.loading}
        showLoadingSpinner
        className="mt-4 md:mt-0 md:mx-auto"
      />
    </div>
  );

  return (
    <div className="w-full">
      <div className="flex items-center justify-center gap-2 p-4">
        {tabs.map((tab) => (
          <Button
            key={tab.tabValue}
            variant={activeTab === tab.tabValue ? 'secondary' : 'ghost'}
            size="small"
            onClick={() => {
              setActiveTab(tab.tabValue);
              onTabChange?.(tab.tabValue);
            }}
            className={cn(
              'px-3 py-1.5 rounded text-[12px] font-medium text-white',
              activeTab === tab.tabValue
                ? 'text-foreground'
                : 'text-foreground/80 hover:text-foreground/60 hover:bg-foreground/10',
            )}
          >
            {tab.tabLabel}
          </Button>
        ))}
      </div>
      <div>{activeTabData?.tabBody}</div>
      {paginationData && (
        <div>
          {renderPagination
            ? renderPagination(paginationData)
            : renderDefaultPagination(paginationData)}
        </div>
      )}
    </div>
  );
};
